import { appConfigStore } from 'Models/AppConfigStore';
import { useEffect, useRef } from 'react';
import * as React from 'react';
import { observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';

import { store } from 'Models/Store';
import { processManualRefund } from 'Services/Api/_HumanWritten/BookingService/BookingService';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import alertToast from 'Util/ToastifyUtils';
import { stringNotEmpty } from 'Util/TypeGuards';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import { TextArea } from 'Views/Components/TextArea/TextArea';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';

export interface RefundData {
	id: string;
	amount: number;
}

export interface RefundNoteModel {
	note: string;
	noteError: string;
}

type ManualRefundModalProps = {
	refund: RefundData;
	refresh?: () => void;
	onClose: () => void;
};

// Helper function to validate note
const validateNote = (noteModel: RefundNoteModel): boolean => {
	let valid = true;

	runInAction(() => {
		if (appConfigStore.manualRefundNoteRequired && (!noteModel.note || noteModel.note.trim().length === 0)) {
			noteModel.noteError = 'Note is required';
			valid = false;
			return;
		}

		if (noteModel.note.trim().length > 500) {
			noteModel.noteError = 'Note must be less than 500 characters';
			valid = false;
			return;
		}

		noteModel.noteError = '';
	});

	return valid;
};

// Create an observer component for the modal content to ensure reactivity
const ManualRefundModalContent = observer(
	({ refund, noteModel }: { refund: RefundData; noteModel: RefundNoteModel }) => {
		return (
			<div className="manual-refund-modal-content">
				<MessageBox status="warning" slim>
					{'This can not be undone so please confirm you are refunding the correct booking.'}
				</MessageBox>
				<p>
					Please confirm you have refunded
					<strong> {formatPriceDisplay(refund.amount, false, true)}</strong> to the customer.
				</p>

				<div className="form-group">
					<TextArea
						id="refund-note"
						label="Refund Note"
						model={noteModel}
						modelProperty="note"
						isRequired={appConfigStore.manualRefundNoteRequired}
					// Handed to the field rather than rendered as a loose div below it: InputWrapper
					// puts the message directly under the input and marks the group with
					// `input-group--error`. The old sibling div used `invalid-feedback`, which has no
					// stylesheet anywhere in the app, so it printed as plain text under the counter.
					errors={noteModel.noteError || undefined}
						placeholder="Please provide details about this manual refund (e.g., method used, reference number, etc.)"
						onAfterChange={e => {
							// Use runInAction to ensure MobX tracks the changes properly
							runInAction(() => {
								noteModel.note = e.target.value;
								// Clear error when user starts typing
								if (noteModel.noteError) {
									noteModel.noteError = '';
								}
							});
						}}
					/>
					<div className="character-count">{noteModel.note.length}/500</div>
					</div>
			</div>
		);
	},
);

// Alternative approach: Helper function that returns an observer component
const buildManualRefundModalContent = (refund: RefundData, noteModel: RefundNoteModel): JSX.Element => {
	return <ManualRefundModalContent refund={refund} noteModel={noteModel} />;
};

export const ManualRefundModal: React.FC<ManualRefundModalProps> = observer(({ refund, refresh, onClose }) => {
	const modalShownRef = useRef(false);

	// Helper function to show loading modal
	const showLoadingModal = (text: string, disableButtons = true) => {
		return newConfirmModal(
			'Processing Manual Refund',
			<div className="loader-container">
				<div className="spinner-dots">
					<div className="dot" />
					<div className="dot" />
					<div className="dot" />
				</div>
				<div className="loading-text dots">{text}</div>
			</div>,
			{
				cancelText: 'Back',
				confirmText: 'Confirm',
				actionClassName: 'manual-refund-modal',
				resolveOnCancel: false,
				disableConfirmButton: disableButtons,
				disableCancelButton: disableButtons,
			},
		);
	};

	useEffect(() => {
		if (!modalShownRef.current) {
			modalShownRef.current = true;

			const noteModel = observable({
				note: '',
				noteError: '',
			});

			newConfirmModal('Set to refunded', buildManualRefundModalContent(refund, noteModel), {
				cancelText: 'Cancel',
				confirmText: 'Confirm Refund',
				actionClassName: 'manual-refund-modal',
				resolveOnCancel: false,
				onBeforeConfirm: async () => {
					// Validate the note before proceeding
					const isValid = validateNote(noteModel);
					if (!isValid) {
						return false; // Keep modal open for validation errors
					}

					try {
						const processingModalPromise = showLoadingModal('Processing manual refund...');

						await processManualRefund(refund.id, noteModel.note.trim());

						store.modal.hide();

						alertToast('Successfully completed manual refund', 'success');

						// Refresh data if callback provided
						if (refresh) {
							refresh();
						}

						onClose?.();

						return true;
					} catch (error: any) {
						store.modal.hide();

						console.error('Error processing manual refund:', error);
						if (stringNotEmpty(error?.response?.data)) {
							alertToast(`Error processing manual refund: ${error?.response?.data}`, 'error');
							if (refresh) {
								refresh();
							}
						} else {
							alertToast('An error occurred while processing the manual refund', 'error');
						}

						onClose?.();

						return false;
					}
				},
			}).catch(() => {
				// User cancelled - close parent modal
				onClose?.();
			});
		}
	}, [refund, refresh, onClose]);

	return null;
});
